import { useState } from "react";
import { Button } from "@/components/ui/button";
import { PageHeader, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { PageTabs } from "@/components/layout/page-tabs";
import { NovaOperacaoDialog } from "@/components/manejo/nova-operacao-dialog";
import { MinhasOperacoes } from "@/components/manejo/minhas-operacoes";
import { SemanaOperacoes } from "@/components/manejo/semana-operacoes";
import { ListaPendencias } from "@/components/manejo/lista-pendencias";
import { FrentesConfig } from "@/components/manejo/frentes-config";
import { useSearch } from "wouter";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { usePode } from "@/lib/permissoes";
import { useAuth } from "@/lib/auth-context";
import { guiaInicial, GUIAS_OPERACOES, type GuiaOperacoes } from "@shared/manejo-guia";
import { CalendarRange, ClipboardCheck, ClipboardList, Plus, Settings } from "lucide-react";

// /manejo/operacoes — a casca.
//
// Guias: Lista (a fila por frente) · Minhas (o operador) · Semana (quem planeja).
// Backlog, Kanban e Hoje saíram: o backlog é a própria Lista (item sem data), o
// Kanban era provisório e o "Hoje" virou a guia Minhas.
//
// A guia mora na URL (`?guia=`, CLAUDE.md §4) e NÃO é lembrada em localStorage, de
// propósito. Sem `guia`, quem não planeja e está ligado a um funcionário abre em
// Minhas, e os demais na Lista (`guiaInicial`). Links antigos (`?vista=`, `guia=hoje`)
// e valores desconhecidos caem num padrão — sem tela branca.

const PADRAO_PAGINA = { guia: "" as string };

export default function ManejoOperacoes() {
  const { definir } = useEstadoNaUrl(PADRAO_PAGINA);
  const search = useSearch();
  const { user } = useAuth();
  const podePlanejar = usePode("manejo.planejar");
  const podeCriar = usePode("manejo.criar");
  const guia: GuiaOperacoes = guiaInicial({
    guia: new URLSearchParams(search).get("guia"),
    vista: new URLSearchParams(search).get("vista"),
    funcionarioId: user?.funcionarioId ?? null,
    podePlanejar,
  });
  const podeConfigurar = usePode("manejo.frentes.configurar");
  const [novaOperacaoAberta, setNovaOperacaoAberta] = useState(false);
  const [frentesAberto, setFrentesAberto] = useState(false);
  // Data pré-selecionada pelo `+` de uma coluna da Semana. `null` = o botão do
  // cabeçalho, que abre no default do formulário.
  const [dataNovaOperacao, setDataNovaOperacao] = useState<string | null>(null);
  // Frente pré-selecionada no diálogo: a do chip da Lista (`?frente=`), ou a do "+" da seção.
  const [frenteNovaOperacao, setFrenteNovaOperacao] = useState<string | null>(null);

  const frenteDoChip = (() => {
    const f = new URLSearchParams(search).get("frente");
    return guia === "lista" && f && f !== "sem" ? f : null;
  })();

  const abrirNovaOperacao = (data?: string, frenteId?: string | null) => {
    setDataNovaOperacao(data ?? null);
    setFrenteNovaOperacao(frenteId ?? frenteDoChip);
    setNovaOperacaoAberta(true);
  };

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <PageHeader
        titulo="Operações"
        descricao="Pendências por frente, fertirrigação, pulverização e tratos"
        acao={
          <div className="flex items-center gap-2">
            {podeConfigurar && (
              <Button variant="outline" className={ALVO_TOQUE_BOTAO} onClick={() => setFrentesAberto(true)} aria-label="Frentes de trabalho" data-testid="btn-frentes">
                <Settings className="h-4 w-4" />
              </Button>
            )}
            {/* Criar é de quem tem manejo.criar (inclui o operador, que cria o item simples:
                título, frente, data). Tipo de trabalho/setores/insumos ficam para quem
                planeja — o diálogo esconde, e a API recusa. Leitura não vê o botão. */}
            {podeCriar && (
              <Button className={ALVO_TOQUE_BOTAO} onClick={() => abrirNovaOperacao()} data-testid="btn-nova-operacao">
                <Plus className="h-4 w-4 mr-1" />Nova operação
              </Button>
            )}
          </div>
        }
      />

      <PageTabs
        abas={[
          { id: "lista", label: "Lista", icone: ClipboardList },
          { id: "minhas", label: "Minhas", icone: ClipboardCheck },
          { id: "semana", label: "Semana", icone: CalendarRange },
        ]}
        ativa={guia}
        onChange={id => definir({ guia: (GUIAS_OPERACOES as readonly string[]).indexOf(id) !== -1 ? id : "" })}
      />

      {/* Criação com atividade/setor/insumo — mesmo commit do agente, origem 'manual'.
          Fica na CASCA porque as guias todas oferecem "Nova operação". */}
      {podeCriar && (
        <NovaOperacaoDialog
          open={novaOperacaoAberta}
          onOpenChange={setNovaOperacaoAberta}
          dataInicial={dataNovaOperacao}
          frenteInicial={frenteNovaOperacao}
        />
      )}

      {podeConfigurar && <FrentesConfig aberto={frentesAberto} onOpenChange={setFrentesAberto} />}

      {guia === "lista" && (
        <ListaPendencias
          onConfigurar={() => setFrentesAberto(true)}
          onNovaOperacao={frenteId => abrirNovaOperacao(undefined, frenteId)}
        />
      )}
      {guia === "minhas" && <MinhasOperacoes />}
      {guia === "semana" && <SemanaOperacoes onNovaOperacaoEm={data => abrirNovaOperacao(data)} />}
    </div>
  );
}
